import type { SaRole } from '@/mocks/saRolesPermissions';
import { HIERARCHY_LABELS } from './roleLabels';

interface Props {
  roles: SaRole[];
  onSelect: (role: SaRole) => void;
  onEdit: (role: SaRole) => void;
  onDuplicate: (role: SaRole) => void;
  onToggleActive: (role: SaRole) => void;
}

export default function RoleTable({ roles, onSelect, onEdit, onDuplicate, onToggleActive }: Props) {
  if (roles.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-shield-user-line text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">No se encontraron roles</p>
        <p className="text-foreground-400 text-xs">Probá ajustando los filtros de búsqueda.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Rol</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Nivel</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Tipo</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
              <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Usuarios</th>
              <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Permisos</th>
              <th className="text-center px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Críticos</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Última actualización</th>
              <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => {
              const permCount = role.permissions.reduce((sum, p) => sum + p.actions.length, 0);
              const criticalCount = role.criticalPermissions.length;

              return (
                <tr
                  key={role.id}
                  className="border-b border-background-200/60 hover:bg-background-100/30 transition-colors cursor-pointer"
                  onClick={() => onSelect(role)}
                >
                  {/* Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: role.color ? `${role.color}14` : 'var(--background-200)', color: role.color || '#6b7280' }}
                      >
                        <i className={`${role.icon} text-sm`}></i>
                      </div>
                      <div className="min-w-0">
                        <span className="text-foreground-800 text-sm font-semibold block truncate">{role.name}</span>
                        <span className="text-foreground-400 text-[10px] block truncate">{role.description.slice(0, 68)}{role.description.length > 68 ? '...' : ''}</span>
                      </div>
                    </div>
                  </td>

                  {/* Hierarchy */}
                  <td className="px-4 py-3">
                    <span className="text-foreground-500 text-xs font-medium">{HIERARCHY_LABELS[role.hierarchyLevel] || `Nivel ${role.hierarchyLevel}`}</span>
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${role.isSystem ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-violet-50 text-violet-700 border border-violet-200'}`}>
                      <i className={`${role.isSystem ? 'ri-git-repository-line' : 'ri-paint-brush-line'} text-[9px]`}></i>
                      {role.isSystem ? 'Sistema' : 'Custom'}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${role.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-foreground-100 text-foreground-500 border border-foreground-200'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${role.active ? 'bg-emerald-500' : 'bg-foreground-400'}`}></div>
                      {role.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>

                  {/* User count */}
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-background-100">
                      <i className="ri-user-line text-foreground-400 text-[9px]"></i>
                      <span className="text-foreground-700 text-xs font-bold">{role.userCount.toLocaleString()}</span>
                    </div>
                  </td>

                  {/* Permissions */}
                  <td className="px-4 py-3 text-center">
                    <span className="text-foreground-700 text-xs font-bold">{permCount}</span>
                    <span className="text-foreground-400 text-[10px] ml-0.5">acciones</span>
                  </td>

                  {/* Critical */}
                  <td className="px-4 py-3 text-center">
                    {criticalCount > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200">
                        <i className="ri-alert-line text-rose-500 text-[9px]"></i>
                        <span className="text-rose-600 text-[11px] font-bold">{criticalCount}</span>
                      </span>
                    ) : (
                      <span className="text-foreground-300 text-xs">—</span>
                    )}
                  </td>

                  {/* Updated */}
                  <td className="px-4 py-3">
                    <span className="text-foreground-400 text-xs">{role.updatedAt}</span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onEdit(role)}
                        className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                        title="Editar rol"
                      >
                        <i className="ri-edit-line text-foreground-500 text-xs"></i>
                      </button>
                      <button
                        onClick={() => onDuplicate(role)}
                        className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                        title="Duplicar rol"
                      >
                        <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
                      </button>
                      <button
                        onClick={() => onToggleActive(role)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${role.active ? 'bg-amber-50 hover:bg-amber-100' : 'bg-emerald-50 hover:bg-emerald-100'}`}
                        title={role.active ? 'Desactivar rol' : 'Activar rol'}
                      >
                        <i className={`${role.active ? 'ri-pause-circle-line text-amber-500' : 'ri-play-circle-line text-emerald-500'} text-xs`}></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}